import { useState } from 'react'
import type { RecordedByKind } from '@domain/product'
import { labelDe } from '@domain/product'
import type { NovoRecado } from '@domain/recado'
import { recadoValido } from '@domain/recado'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { EscolherAutor } from '@ui/shell/EscolherAutor'

/**
 * Deixar um recado na casa.
 *
 * ## Um `<textarea>`, e não um campo de uma linha
 *
 * O que se cola aqui é o resultado de uma busca: um parágrafo, uma lista de
 * links, uma tabela em markdown. Um `<input>` de uma linha esconderia o que
 * acabou de ser colado, e o gesto seguinte seria rolar dentro de um campo de
 * 40px para conferir se colou inteiro.
 *
 * ## O autor é a mesma pergunta do ponto de preço
 *
 * `EscolherAutor` veio de `RegistrarPreco`, e a semântica é idêntica: `ai`
 * significa **este texto é de um agente**, não que um agente tenha gravado.
 * Quem grava é sempre quem está logado, e o adapter carimba isso à parte.
 */
export function FolhaDeRecado({
  doUsuario,
  onFechar,
  onGravar,
}: {
  /** O nome de quem está logado, para rotular a opção "eu". */
  doUsuario: string | null
  onFechar: () => void
  onGravar: (novo: NovoRecado) => Promise<void>
}) {
  const [texto, setTexto] = useState('')
  const [quem, setQuem] = useState<RecordedByKind>('user')
  const [nome, setNome] = useState('')
  const [gravando, setGravando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  const podeGravar = recadoValido(texto) && !gravando

  return (
    <FolhaDeBaixo titulo="Novo recado" onFechar={onFechar}>
      <h2 className="titulo mb-3 text-base">Novo recado</h2>

      <label className="block text-sm">
        <span className="mb-1 block text-xs text-[var(--text-muted)]">Recado</span>
        <textarea
          value={texto}
          rows={6}
          aria-label="Texto do recado"
          placeholder="Achei a 39,90 no Muffato — https://…"
          className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1.5 text-base"
          onChange={(e) => {
            setTexto(e.target.value)
          }}
        />
      </label>

      <div className="mt-3">
        <EscolherAutor
          legenda="Quem escreveu"
          quem={quem}
          nome={nome}
          doUsuario={doUsuario}
          onQuem={setQuem}
          onNome={setNome}
        />
      </div>

      {falhou && (
        <p role="alert" className="mt-3 text-xs text-[var(--text-muted)]">
          Não foi possível salvar agora.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Botao
          type="button"
          tom="primario"
          disabled={!podeGravar}
          onClick={() => {
            setGravando(true)
            setFalhou(false)
            void onGravar({
              texto,
              // `id` só quando é membro — é o mesmo contrato de `RecordedBy`, e
              // é o adapter quem sabe o uid. Aqui vai `null` e ele carimba.
              autor: { kind: quem, id: null, label: labelDe(quem, nome, doUsuario) },
            })
              .then(onFechar)
              .catch(() => {
                setFalhou(true)
                setGravando(false)
              })
          }}
        >
          Deixar o recado
        </Botao>
        <Botao type="button" onClick={onFechar}>
          Cancelar
        </Botao>
      </div>
    </FolhaDeBaixo>
  )
}
